import { useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Field,
  Input,
  MessageBar,
  Select,
  MessageBarBody,
  makeStyles,
  tokens,
} from "@fluentui/react-components";
import { ESITI, type Esito } from "@app/schema";
import { formatNumberInput, parseNumber } from "../lib/format";
import { useData } from "../data/DataProvider";
import { salvaLotto, type Gara, type Lotto } from "../data/gare";
import { describeError } from "./ErrorState";

const useStyles = makeStyles({
  form: { display: "flex", flexDirection: "column", gap: tokens.spacingVerticalM },
  two: { display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 2fr)", gap: tokens.spacingHorizontalM, alignItems: "start", "& > *": { minWidth: 0 } },
  full: { width: "100%", minWidth: 0 },
});

/** Aggiunta o modifica di un lotto. */
export function LottoDialog(props: { gara: Gara; lotto?: Lotto; altri: Lotto[]; onClose: (saved: boolean) => void }) {
  const s = useStyles();
  const data = useData();
  const l = props.lotto?.fields;
  const prossimo = Math.max(0, ...props.altri.map((x) => x.fields.Numero ?? 0)) + 1;
  const [numero, setNumero] = useState(String(l?.Numero ?? prossimo));
  const [oggetto, setOggetto] = useState(l?.Title ?? "");
  const [cig, setCig] = useState(l?.CIG ?? "");
  const [importo, setImporto] = useState(formatNumberInput(l?.Importo));
  const [esito, setEsito] = useState<Esito | "">((l?.Esito as Esito) ?? "");
  const [aggiudicato, setAggiudicato] = useState(formatNumberInput(l?.ImportoAggiudicato));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string>();
  const bloccato = !!l?.CartellaId;

  async function save() {
    const n = Number(numero);
    const imp = parseNumber(importo);
    const agg = parseNumber(aggiudicato);
    if (aggiudicato.trim() && agg === undefined) return setErr("Importo aggiudicato non valido.");
    if (!Number.isInteger(n) || n < 1) return setErr("Il numero del lotto deve essere un intero da 1 in su.");
    if (importo.trim() && imp === undefined) return setErr("Importo non valido.");
    setBusy(true);
    setErr(undefined);
    try {
      await salvaLotto(data, props.gara, { Title: oggetto, Numero: n, CIG: cig.trim() || undefined, Importo: imp, Esito: esito || undefined, ImportoAggiudicato: agg }, props.lotto, props.altri);
      props.onClose(true);
    } catch (e) {
      setErr(describeError(e).detail);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open onOpenChange={(_, d) => !d.open && props.onClose(false)}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>{props.lotto ? `Lotto ${l?.Numero}` : "Nuovo lotto"}</DialogTitle>
          <DialogContent className={s.form}>
            {err && (
              <MessageBar intent="error">
                <MessageBarBody>{err}</MessageBarBody>
              </MessageBar>
            )}
            <div className={s.two}>
              <Field label="Numero" required hint={bloccato ? "Cartella già creata: non modificabile" : undefined}>
                <Input inputMode="numeric" value={numero} disabled={bloccato} onChange={(_, d) => setNumero(d.value)} />
              </Field>
              <Field label="CIG">
                <Input value={cig} maxLength={40} onChange={(_, d) => setCig(d.value)} />
              </Field>
            </div>
            <Field label="Oggetto del lotto">
              <Input value={oggetto} maxLength={255} placeholder={`Lotto ${numero}`} onChange={(_, d) => setOggetto(d.value)} />
            </Field>
            <Field label="Importo a base d'asta (€)">
              <Input inputMode="decimal" value={importo} placeholder="0,00" onChange={(_, d) => setImporto(d.value)} />
            </Field>
            <div className={s.two}>
              <Field label="Esito">
                <Select className={s.full} value={esito} onChange={(_, d) => setEsito(d.value as Esito | "")}>
                  <option value="">Non ancora noto</option>
                  {ESITI.map((x) => (
                    <option key={x}>{x}</option>
                  ))}
                </Select>
              </Field>
              {esito === "Aggiudicata" && (
                <Field label="Importo aggiudicato (€)">
                  <Input inputMode="decimal" value={aggiudicato} placeholder="0,00" onChange={(_, d) => setAggiudicato(d.value)} />
                </Field>
              )}
            </div>
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={() => props.onClose(false)} disabled={busy}>
              Annulla
            </Button>
            <Button appearance="primary" onClick={save} disabled={busy}>
              {busy ? "Salvataggio…" : "Salva"}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
